// Repoworks frontend UI theme tokens — dark, monochrome.
//
// IMPORTANT: This is the FRONTEND PRODUCT SKIN only. It is NOT the Appli
// document brand. The Appli brand lives in packages/brand-tokens/tokens.json
// and is read by docgen + backend over the filesystem to render documents —
// do not conflate the two or move values between them.
//
// The look: a near-black page, content on flat #1A1A1A panels, one green
// accent for status, red only for delete and stop. No gradients, no
// shadows.
export const repoTokens = {
  colors: {
    page: "#0C0C0C", // the page behind the panels; the app bar sits on it
    background: "#0C0C0C", // MUI background.default (= page)
    // Kept as a name for the few places that measured against the old
    // black app bar: the bar is transparent now, so it is the page.
    chrome: "#0C0C0C",
    panel: "#1A1A1A", // the shell's panels
    surface: "#1A1A1A", // cards, drawers, inputs (MUI "paper") = panel
    elevated: "#242424", // composer, menus, popovers: what sits above a panel
    surfaceHover: "#242424", // hover (= elevated)
    elevatedHover: "#2E2E2E", // hover on an elevated control
    line: "#404040", // borders, dividers — ~1.9:1 on the panels, so card / divider / input edges are visible
    text: "#FFFFFF", // primary text
    muted: "#BCBCBC", // secondary text, card descriptions
    // Disabled text, also used for quiet meta lines: 5.2:1 on a panel,
    // 4.6:1 on the elevated surface (#717171 was 3.6:1 on a panel).
    disabled: "#8C8C8C",
    icon: "#FFFFFF", // icons are white
    primary: "#FFFFFF", // CTA fill (white "pill" buttons)
    primaryText: "#000000", // text on primary fill
    secondary: "#2A2A2A", // secondary / toggle fill
    focus: "#FFFFFF", // WCAG focus ring on dark
    // The one accent: status ("Edits files", "Asks for approval", an
    // active session's rule). Deliberately NOT Appli green (#3FC38B):
    // that one belongs to the Appli document brand (packages/brand-tokens).
    accent: "#45D69A",
    // Red: delete and stop only.
    danger: "#F2555A",
    // Functional status colours for MUI's palette slots.
    error: "#F2555A",
    warning: "#E0A23B", // real warnings only, never a status marker
    success: "#45D69A", // = accent
    info: "#5B9BF2",
  },
  fonts: {
    heading: `"Inter", "Segoe UI", system-ui, sans-serif`,
    body: `"Inter", "Segoe UI", system-ui, sans-serif`,
    // Repo, branch, sha and version.
    mono: `"DejaVu Sans Mono", "Menlo", monospace`,
  },
  // Type scale in px. ``base`` is body text (MUI body1).
  type: {
    base: 15,
    startHeading: { size: 28, weight: 600 }, // the start page's greeting
    panelTitle: { size: 22, weight: 600 },
    overline: { size: 11, weight: 700, letterSpacing: 1.4 },
    meta: 13, // timestamps, counts, secondary lines (MUI caption)
    messageBody: 16, // chat message text
    cardTitle: { size: 15, weight: 600 },
    cardDescription: { size: 14, weight: 400 }, // in ``colors.muted``
  },
  radii: { sm: 6, md: 10, lg: 14 },
  // The panel shell (AppShell).
  shell: {
    panelRadius: 14,
    gap: 15, // page edge to panel, and panel to panel
    phoneGap: 8, // below the drawer breakpoint
    // Sides: = gap, so the app bar's controls line up with the panels'
    // outer edges (and a full-width banner under it).
    appBarPadding: 15,
    // Page top to the app bar's controls. Tighter than the sides, so the
    // bar sits about as far from the page top as from the panels below.
    appBarTop: 12,
    // Inside a panel: the left column's content, and the centre panel's.
    leftColumnPadding: 16,
    panelPadding: 32,
    logoHeight: 26,
    leftColumn: 404,
    rightColumn: 404,
    collapseButton: 28,
  },
  // The start page's intent grid.
  startPage: {
    cardMaxWidth: 348,
    cardMinWidth: 240, // below this a row drops a column
    cardGap: 18,
  },
} as const;

export default repoTokens;
